import type { MenuItem } from "@replo/design-system/components/menu/Menu";
import type { Asset } from "schemas/generated/asset";

import * as React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { analytics } from "@editor/infra/analytics";
import { assetRouter, assetUtils } from "@editor/trpc/asset";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Menu } from "@replo/design-system/components/menu/Menu";
import Popover from "@replo/design-system/components/popover/Popover";
import { Ellipsis, PenLine, Trash } from "lucide-react";

import AssetCardRenameForm from "../AssetCardRenameForm";

export const AssetCardActions: React.FC<{
  className?: string;
  asset: Asset;
  isMenuOpen: boolean;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
  setActionPopoverContent: (
    actionPopoverContent: "delete" | "rename" | null,
  ) => void;
  actionPopoverContent: "delete" | "rename" | null;
}> = ({
  className,
  asset,
  isMenuOpen,
  setIsMenuOpen,
  setActionPopoverContent,
  actionPopoverContent,
}) => {
  const projectId = useCurrentProjectId();
  const { mutateAsync: deleteAsset } = assetRouter.delete.useMutation({
    onSuccess: () => {
      analytics.logEvent("asset.deleted", {
        projectId: projectId!,
        assetId: asset.id,
      });
      void assetUtils.list.invalidate();
      setIsMenuOpen(false);
      setActionPopoverContent(null);
    },
  });
  const { mutateAsync: updateAsset } = assetRouter.update.useMutation({
    onSuccess: () => {
      analytics.logEvent("asset.updated", {
        projectId: projectId!,
        assetId: asset.id,
      });
      void assetUtils.list.invalidate();
      setIsMenuOpen(false);
      setActionPopoverContent(null);
    },
  });

  const onDeleteAsset = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!projectId) {
      return;
    }

    await deleteAsset({
      assetId: asset.id,
      projectId,
    });
  };

  const onRenameAsset = async (assetName: string) => {
    if (!projectId) {
      return;
    }

    await updateAsset({
      assetId: asset.id,
      projectId,
      name: assetName,
    });
  };

  const menuActions: MenuItem[] = [
    {
      type: "leaf",
      id: "edit",
      variant: "default",
      size: "sm",
      startEnhancer: <PenLine size={12} />,
      title: "Rename",
      onSelect: () => setActionPopoverContent("rename"),
    },
    {
      type: "leaf",
      id: "delete",
      variant: "default",
      size: "sm",
      startEnhancer: <Trash size={12} />,
      title: "Delete",
      onSelect: () => setActionPopoverContent("delete"),
    },
  ];

  return (
    <Popover.Root
      isOpen={Boolean(actionPopoverContent)}
      onOpenChange={(open) => {
        if (!open) {
          setActionPopoverContent(null);
        }
      }}
    >
      <Popover.Content
        shouldPreventDefaultOnInteractOutside
        onRequestClose={() => setActionPopoverContent(null)}
        side="right"
        align="end"
        sideOffset={4}
        title={
          actionPopoverContent === "delete" ? "Delete asset" : "Rename asset"
        }
        className="flex flex-col"
      >
        <div className="flex flex-col gap-3">
          {actionPopoverContent === "delete" ? (
            <AssetCardDeleteForm
              onDeleteAsset={(e: React.MouseEvent<HTMLButtonElement>) =>
                void onDeleteAsset(e)
              }
              onCancel={() => setActionPopoverContent(null)}
            />
          ) : (
            <AssetCardRenameForm
              originalAssetName={asset.name}
              onRenameAsset={(assetName) => void onRenameAsset(assetName)}
            />
          )}
        </div>
      </Popover.Content>
      <Popover.Trigger className={className}>
        <Menu
          isOpen={isMenuOpen}
          onRequestOpen={() => setIsMenuOpen(true)}
          onRequestClose={() => setIsMenuOpen(false)}
          align="start"
          size="sm"
          side="right"
          sideOffset={4}
          disableTriggerFocusOnClose
          items={menuActions}
          contentClassNames="w-40"
          trigger={
            <IconButton
              variant="tertiary"
              size="sm"
              layoutClassName="h-4 w-4"
              icon={<Ellipsis size={12} />}
              aria-hidden
            />
          }
        />
      </Popover.Trigger>
    </Popover.Root>
  );
};

const AssetCardDeleteForm: React.FC<{
  onDeleteAsset: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onCancel: () => void;
}> = ({ onDeleteAsset, onCancel }) => {
  return (
    <>
      <div className="text-sm">
        If this asset is being used on a page, deleting it will break the asset
        until it is replaced.
      </div>
      <div className="flex gap-2">
        <Button variant="danger" size="sm" onClick={onDeleteAsset}>
          Delete
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </>
  );
};
